import { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, Send, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';


const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  setIsSubmitting(true);

  try {
    const form = e.target;
    const data = new FormData(form);

    await fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams(data).toString(),
    });

    toast.success("Message sent successfully!");
    setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
  } catch (err) {
    toast.error("Failed to send message. Try again.");
  } finally {
    setIsSubmitting(false);
  }
};

  const contactInfo = [
    {
      icon: <Phone className="w-6 h-6" />,
      title: 'Phone',
      details: '+91 96114 75342',
      link: 'tel:+919611475342',
      action: 'Call Now'
    },
    {
      icon: <MessageCircle className="w-6 h-6" />,
      title: 'WhatsApp',
      details: '+91 96114 75342',
      link: 'https://wa.me/919611475342',
      action: 'Message Us'
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: 'Email',
      details: 'shettyrains40@gmail.com',
      link: 'mailto:shettyrains40@gmail.com',
      action: 'Send Email'
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: 'Hours',
      details: '11:45 AM – 10:00 PM',
      subtext: 'Open Every Day'
    },
  ];

  return (
    <div className="page-wrapper pt-24" data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative py-16 md:py-24 px-6 md:px-12 bg-[#F0EBE0]" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto text-center">
          <motion.span
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-accent text-2xl text-[#E07A5F] block mb-2"
          >
            Get in Touch
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-6xl font-black text-[#4A3B32] mb-4"
            style={{ fontFamily: 'Fraunces, serif' }}
          >
            Contact Us
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-[#6D5D53] max-w-xl mx-auto"
          >
            Have feedback, questions, or just want to say hi? We'd love to hear from you!
          </motion.p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 px-6 md:px-12 bg-[#F9F7F2]" data-testid="contact-info-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 text-center shadow-sm hover:shadow-md transition-shadow duration-300"
              >
                <div className="w-14 h-14 bg-[#F0EBE0] rounded-full flex items-center justify-center mx-auto mb-4 text-[#E07A5F]">
                  {info.icon}
                </div>
                <h3 className="font-bold text-[#4A3B32] mb-2" style={{ fontFamily: 'Fraunces, serif' }}>
                  {info.title}
                </h3>
                <p className="text-[#6D5D53] mb-1 text-sm">{info.details}</p>
                {info.subtext && <p className="text-[#6D5D53]/70 text-sm">{info.subtext}</p>}
                {info.link && (
                  <a
                    href={info.link}
                    target={info.link.startsWith('http') ? '_blank' : undefined}
                    rel={info.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                    className="inline-block mt-3 text-[#E07A5F] font-medium hover:underline"
                    data-testid={`contact-${info.title.toLowerCase()}-link`}
                  >
                    {info.action} →
                  </a>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Order Platforms */}
      <section className="py-8 px-6 md:px-12 bg-[#F9F7F2]" data-testid="order-platforms">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <p className="text-center text-[#6D5D53] mb-4 font-medium">Order from your favorite platform:</p>
            <div className="flex items-center justify-center gap-6">
              <a 
                href="https://www.swiggy.com/menu/1242857?source=sharing" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-[#F9F7F2] rounded-xl px-5 py-3 hover:scale-105 transition-transform"
                data-testid="contact-swiggy-link"
              >
                <img src="https://customer-assets.emergentagent.com/job_f7742170-5b8f-447f-9d8a-e99e46f1b0c0/artifacts/dfsl17ui_images.jfif" alt="Swiggy" className="h-6 w-6 object-contain" />
              </a>
              <a 
                href="https://zomato.onelink.me/xqzv/rcql1v1i" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-[#F9F7F2] rounded-xl px-5 py-3 hover:scale-105 transition-transform"
                data-testid="contact-zomato-link"
              >
                <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/7/75/Zomato_logo.png/150px-Zomato_logo.png" alt="Zomato" className="h-6" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Form + Map Section */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#F9F7F2]" data-testid="form-map-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="bg-white rounded-3xl p-8 shadow-lg">
                <h2 
                  className="text-2xl md:text-3xl font-bold text-[#4A3B32] mb-6"
                  style={{ fontFamily: 'Fraunces, serif' }}
                >
                  Send us a Message
                </h2>
              <form
  name="contact"
  method="POST"
  data-netlify="true"
  netlify-honeypot="bot-field"
  onSubmit={handleSubmit}
  className="space-y-6"
  data-testid="contact-form"
>
                <input type="hidden" name="form-name" value="contact" />

<p hidden>
  <input name="bot-field" />
</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name" className="text-[#4A3B32] font-medium">Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        placeholder="Your name"
                        className="mt-2 rounded-xl border-[#E6DCC8] focus:border-[#E07A5F] focus:ring-[#E07A5F]"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-[#4A3B32] font-medium">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="your@email.com"
                        className="mt-2 rounded-xl border-[#E6DCC8] focus:border-[#E07A5F] focus:ring-[#E07A5F]"
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="phone" className="text-[#4A3B32] font-medium">Phone</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+91 98765 43210"
                        className="mt-2 rounded-xl border-[#E6DCC8] focus:border-[#E07A5F] focus:ring-[#E07A5F]"
                        data-testid="contact-phone-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="subject" className="text-[#4A3B32] font-medium">Subject *</Label>
                      <Input
                        id="subject"
                        name="subject"
                        type="text"
                        value={formData.subject}
                        onChange={handleInputChange}
                        required
                        placeholder="What's this about?"
                        className="mt-2 rounded-xl border-[#E6DCC8] focus:border-[#E07A5F] focus:ring-[#E07A5F]"
                        data-testid="contact-subject-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-[#4A3B32] font-medium">Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      placeholder="Your message..."
                      rows={5}
                      className="mt-2 rounded-xl border-[#E6DCC8] focus:border-[#E07A5F] focus:ring-[#E07A5F] resize-none"
                      data-testid="contact-message-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full rounded-full py-4 text-lg font-bold"
                    data-testid="contact-submit-btn"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Sending...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <Send className="w-5 h-5" />
                        Send Message
                      </span>
                    )}
                  </Button>
                </form>
              </div>
            </motion.div>

            {/* Map & Location */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex flex-col gap-6"
            >
              {/* Location Card */}
              <div className="bg-[#4A3B32] rounded-3xl p-8 text-white">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#E07A5F] rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Fraunces, serif' }}>
                      Find Us Here
                    </h3>
                    <p className="text-white/80 leading-relaxed">
                      A1 Cafe<br />
                      NIT Karnataka, Hostel Area<br />
                      Near NITK Campus<br />
                      Surathkal, Mangaluru
                    </p>
                    <a
                      href="https://maps.app.goo.gl/jgFwpAgEZtdiEADL6"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block mt-4 text-[#F2CC8F] font-medium hover:underline"
                      data-testid="get-directions-link"
                    >
                      Get Directions on Google Maps →
                    </a>
                  </div>
                </div>
              </div>

              {/* Cafe Image as Location Visual */}
              <div className="flex-1 min-h-[350px] rounded-3xl overflow-hidden shadow-lg relative">
                <img 
                  src="https://customer-assets.emergentagent.com/job_f7742170-5b8f-447f-9d8a-e99e46f1b0c0/artifacts/0fbya8iq_unnamed.webp"
                  alt="A1 Cafe at Night"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-bold text-lg" style={{ fontFamily: 'Fraunces, serif' }}>A1 Cafe</p>
                      <p className="text-white/80 text-sm">NITK Campus Area</p>
                    </div>
                    <a
                      href="https://maps.app.goo.gl/jgFwpAgEZtdiEADL6"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-[#E07A5F] text-white px-4 py-2 rounded-full font-medium hover:bg-[#d06a4f] transition-colors flex items-center gap-2"
                      data-testid="contact-google-map"
                    >
                      <MapPin className="w-4 h-4" />
                      Directions
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quick Order CTA */}
      <section className="py-16 px-6 md:px-12 bg-[#E07A5F]" data-testid="quick-order-cta">
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            className="text-3xl md:text-4xl font-bold text-white mb-4"
            style={{ fontFamily: 'Fraunces, serif' }}
          >
            Want to Order Instead?
          </h2>
          <p className="text-white/90 mb-8 text-lg">
            Skip the form! Just WhatsApp us your order and we'll have it ready.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://wa.me/919611475342?text=Hi! I'd like to place an order from A1 Cafe"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-btn"
            >
              <Button className="bg-white text-[#4A3B32] hover:bg-[#F9F7F2] rounded-full px-8 py-4 text-lg font-bold">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Order on WhatsApp
              </Button>
            </a>
            <a href="tel:+919611475342" data-testid="contact-call-btn">
              <Button variant="outline" className="rounded-full px-8 py-4 text-lg font-bold border-white text-white hover:bg-white hover:text-[#E07A5F]">
                <Phone className="w-5 h-5 mr-2" />
                Call Now
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
